import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useUsageStats } from "@/hooks/useUsageStats"
import { useUserMap } from "@/hooks/useUserMap"
import { Activity, Gauge, Zap, Wrench } from "lucide-react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"

function getDateRange(days: number) {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - days)
  return {
    from: from.toISOString().split("T")[0],
    to: to.toISOString().split("T")[0],
  }
}

export default function UsageDashboard() {
  const [period, setPeriod] = useState(7)
  const { from, to } = useMemo(() => getDateRange(period), [period])
  const { summary, userRanking, dailyTrend, loading, error } = useUsageStats({
    from,
    to,
  })
  const { resolveEmail } = useUserMap()

  if (error) {
    return (
      <div className="text-center py-10 text-destructive">
        <p>Failed to load usage stats: {error}</p>
      </div>
    )
  }

  const creditFmt = new Intl.NumberFormat("ja-JP")
  const cards = [
    {
      title: "リクエスト合計",
      value: summary?.totalRequests ?? 0,
      icon: Activity,
    },
    {
      title: "Token 合計",
      value: summary?.totalTokens
        ? summary.totalTokens.toLocaleString()
        : "0",
      icon: Zap,
    },
    {
      // 単位をクレジットに統一（Owner UI の上限編集と単位を揃える）。USD 表記は撤去。
      title: "クレジット",
      value: summary?.totalCredits
        ? `${creditFmt.format(summary.totalCredits)} credits`
        : "0 credits",
      icon: Gauge,
    },
    {
      title: "ツール呼び出し",
      value: summary?.totalToolCalls ?? 0,
      icon: Wrench,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">利用状況ダッシュボード</h1>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-3 py-1 rounded text-sm ${
                period === d
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {d}日
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold">{card.value}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily Trend Chart — クレジット（左軸）＋ Token（右軸）。単位はクレジットに統一 */}
      <Card>
        <CardHeader>
          <CardTitle>日別クレジット / Token 使用量</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-64 w-full" />
          ) : dailyTrend.length === 0 ? (
            <p className="text-muted-foreground text-center py-10">
              利用データはありません
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="cr"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => creditFmt.format(v as number)}
                />
                <YAxis
                  yAxisId="tok"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Legend />
                <Area
                  yAxisId="cr"
                  type="monotone"
                  dataKey="credits"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary))"
                  fillOpacity={0.35}
                  name="クレジット"
                />
                <Area
                  yAxisId="tok"
                  type="monotone"
                  dataKey="inputTokens"
                  stackId="tok"
                  stroke="hsl(var(--muted-foreground))"
                  fill="hsl(var(--muted-foreground))"
                  fillOpacity={0.15}
                  name="Input Tokens"
                />
                <Area
                  yAxisId="tok"
                  type="monotone"
                  dataKey="outputTokens"
                  stackId="tok"
                  stroke="hsl(var(--destructive))"
                  fill="hsl(var(--destructive))"
                  fillOpacity={0.15}
                  name="Output Tokens"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* User Ranking Table */}
      <Card>
        <CardHeader>
          <CardTitle>ユーザー利用ランキング</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : userRanking.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              利用データはありません
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">#</th>
                    <th className="text-left py-2 px-3">ユーザー</th>
                    <th className="text-right py-2 px-3">リクエスト</th>
                    <th className="text-right py-2 px-3">Input Tokens</th>
                    <th className="text-right py-2 px-3">Output Tokens</th>
                    <th className="text-right py-2 px-3">Token 合計</th>
                    <th className="text-right py-2 px-3">クレジット</th>
                  </tr>
                </thead>
                <tbody>
                  {userRanking.map((user, i) => (
                    <tr key={user.userId} className="border-b">
                      <td className="py-2 px-3">{i + 1}</td>
                      <td className="py-2 px-3">
                        <div>{resolveEmail(user.userId) || "-"}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {user.userId.slice(0, 12)}...
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        {user.requestCount}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {user.inputTokens.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {user.outputTokens.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        {user.totalTokens.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        {creditFmt.format(user.credits ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
